import React, { useEffect } from 'react';
import { captureConvexError } from '@/lib/sentry';
import { useToast } from '@/components/ui/toaster';

interface ConvexErrorHandlerProps {
  children: React.ReactNode;
}

// Global Convex error handler component
export function ConvexErrorHandler({ children }: ConvexErrorHandlerProps) {
  const { show: toast } = useToast();

  useEffect(() => {
    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // Check if this is a Convex error
      if (isConvexError(error)) {
        event.preventDefault(); // Prevent default browser error handling
        
        console.error('Unhandled Convex error:', error);
        
        // Capture to Sentry with Convex context
        captureConvexError(error, {
          type: 'unhandled_promise_rejection',
          url: window.location.href,
        });
        
        // Show user-friendly toast notification
        showConvexErrorToast(error);
      }
    };

    // Global error handler for synchronous errors
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      
      if (isConvexError(error)) {
        console.error('Unhandled Convex error:', error);
        
        captureConvexError(error, {
          type: 'unhandled_error',
          url: window.location.href,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
        
        showConvexErrorToast(error);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [toast]);

  const isConvexError = (error: any): boolean => {
    if (!error) return false;
    
    // Check error message patterns
    const message = error.message || error.toString();
    const stack = error.stack || '';
    
    return (
      message.includes('CONVEX') ||
      message.includes('[Request ID:') ||
      message.includes('ConvexError') ||
      message.includes('ArgumentValidationError') ||
      message.includes('UnauthorizedError') ||
      stack.includes('convex') ||
      stack.includes('@convex') ||
      error.name === 'ConvexError'
    );
  };

  const showConvexErrorToast = (error: any) => {
    const message = error.message || error.toString();
    
    // Parse error type and show appropriate message
    let title = 'Database Error';
    let description = 'A database error occurred. Please try again.';
    
    if (message.includes('ArgumentValidationError')) {
      title = 'Invalid Data';
      description = 'The data provided is invalid. Please check your input and try again.';
    } else if (message.includes('UnauthorizedError') || message.includes('Not authenticated')) {
      title = 'Authentication Required';
      description = 'Please sign in to continue.';
    } else if (message.includes('Not authorized')) {
      title = 'Access Denied';
      description = 'You don\'t have permission to perform this action.';
    } else if (message.includes('not found') || message.includes('Not found')) {
      title = 'Resource Not Found';
      description = 'The requested resource could not be found.';
    } else if (message.includes('timeout') || message.includes('Timeout')) {
      title = 'Request Timeout';
      description = 'The request took too long to complete. Please try again.';
    } else if (message.includes('rate limit') || message.includes('Rate limit')) {
      title = 'Too Many Requests';
      description = 'Please wait a moment before trying again.';
    }

    // Show toast notification
    toast({
      title,
      description,
    });
  };

  return <>{children}</>;
}

// Hook for manual Convex error handling in components
export function useConvexErrorHandler() {
  const { show: toast } = useToast();

  const handleConvexError = (error: any, context?: Record<string, any>) => {
    console.error('Convex error:', error);
    
    // Capture to Sentry
    captureConvexError(error, {
      ...context,
      component: 'manual_handling',
    });
    
    // Show user notification
    const message = error.message || error.toString();
    
    let title = 'Operation Failed';
    let description = 'An error occurred while processing your request.';
    
    if (message.includes('ArgumentValidationError')) {
      title = 'Invalid Data';
      description = 'Please check your input and try again.';
    } else if (message.includes('UnauthorizedError')) {
      title = 'Authentication Required';
      description = 'Please sign in to continue.';
    } else if (message.includes('Not authorized')) {
      title = 'Access Denied';
      description = 'You don\'t have permission to perform this action.';
    }
    
    toast({
      title,
      description,
    });
  };

  return { handleConvexError };
}
